import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Sales, Masters } from "../lib/api";
import { computeFinanceSummary, isV2Sale } from "../lib/finance";
import { Button } from "../components/ui/button";
import { ArrowLeft, Printer } from "lucide-react";
import { useApp } from "../context/AppContext";
import { fmtDate } from "../lib/auditFormat";

// ──────────────── Formatters ────────────────
const dash = (v) => (v == null || v === "" ? "—" : v);
const inr = (v) =>
  v == null || v === "" || Number.isNaN(Number(v))
    ? "—"
    : `₹${Number(v).toLocaleString("en-IN")}`;

export default function SalesPrint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { locations, users } = useApp();
  const [sale, setSale] = useState(null);
  const [m, setM] = useState({});

  useEffect(() => {
    (async () => setSale(await Sales.get(id)))();
  }, [id]);

  useEffect(() => {
    (async () => {
      const types = [
        "enquiry_source",
        "enquiry_sub_source",
        "finance_company",
        "regn_type",
        "vas_protection_plus",
        "vas_amc",
        "vas_rsa",
      ];
      const out = {};
      await Promise.all(
        types.map(async (t) => {
          out[t] = await Masters.list({ type: t });
        }),
      );
      setM(out);
    })();
  }, []);

  const findM = (type, idVal) =>
    (m[type] || []).find((x) => x.id === idVal);

  // Cumulative on-road / total price — gross sum minus the overall
  // discount captured on payment.discounts.total. The discount can be either
  // an absolute amount (`mode: 'abs'`) or a percentage (`mode: 'pct'`); both
  // are honored here so the printed sheet matches Sales Detail / Accounts.
  //
  // VAS opt-out gate (Feb-2026): any VAS row where the customer has
  // explicitly opted out (`*_opted === false`) contributes 0 to gross, even
  // if a stale price sits in payment. Legacy sales with null/undefined
  // opt-in flags stay opted-in for backwards compatibility.
  const grossTotal = useMemo(() => {
    if (!sale?.payment) return 0;
    const vas = sale.vas || {};
    const blocked = {
      pp:  vas.pp_opted  === false,
      amc: vas.amc_opted === false,
      rsa: vas.rsa_opted === false,
    };
    const keys = [
      "exshowroom",
      "registration",
      "hypothecation",
      "insurance",
      "pp",
      "amc",
      "rsa",
      "accessories",
      "admin_incidental",
    ];
    return keys.reduce((s, k) => s + (blocked[k] ? 0 : (Number(sale.payment[k]) || 0)), 0);
  }, [sale]);

  const discountInfo = useMemo(() => {
    const d = sale?.payment?.discounts?.total;
    const v = Number(d?.value) || 0;
    if (!v) return { applied: 0, label: null };
    const isPct = d?.mode === "pct";
    const applied = isPct ? Math.min(grossTotal, (grossTotal * v) / 100) : Math.min(grossTotal, v);
    return {
      applied,
      label: isPct ? `Discount (${v}%)` : "Discount",
    };
  }, [sale, grossTotal]);

  const totalPrice = Math.max(0, grossTotal - discountInfo.applied);
  // v2 pricing summary — kept unconditionally so we can branch below without
  // touching legacy sales.
  const v2 = useMemo(() => computeFinanceSummary(sale || {}), [sale]);
  const isV2 = isV2Sale(sale);

  if (!sale) {
    return (
      <div className="py-10 text-center text-sm text-slate-500">Loading…</div>
    );
  }

  const loc = locations.find((l) => l.id === sale.location_id);
  const dse = users.find((u) => u.id === sale.dse_id);
  const sheetDate = sale.submitted_at
    ? fmtDate(sale.submitted_at)
    : fmtDate(sale.created_at);

  const vasLabel = (type, idVal, optedKey) => {
    // Opt-out gate — when the customer has explicitly opted out, show that
    // regardless of whether a stale plan id sits on the sale (defensive).
    if (sale?.vas?.[optedKey] === false) return "Not Opted";
    const x = findM(type, idVal);
    return x ? x.name : "—";
  };
  const vasAmount = (key, optedKey) => {
    if (sale?.vas?.[optedKey] === false) return "Not Opted";
    return inr(sale.payment?.[key]);
  };

  const accessoryItems = sale.accessory_items || [];
  const accessoriesSummary =
    accessoryItems.length === 0
      ? "—"
      : accessoryItems.map((a) => a.name).filter(Boolean).join(", ");

  const receipts = sale.payment_receipts || [];

  const regnTypeLabel = (() => {
    const r = findM("regn_type", sale.registration?.regn_type_id);
    if (!r) return "—";
    return r.code ? `${r.code} — ${r.name}` : r.name;
  })();

  const insuranceLabel = (() => {
    if (sale.insurance?.type === "purchased") return "Purchased";
    if (sale.insurance?.type === "customer_self") return "Customer Self";
    return "—";
  })();

  return (
    <div className="min-h-screen bg-white" data-testid="sales-print-page">
      {/* Toolbar — hidden when printing */}
      <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white px-6 py-3 print:hidden">
        <Button
          variant="outline"
          onClick={() => navigate(`/sales/${id}`)}
          className="gap-1.5"
          data-testid="print-back-btn"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
        <div className="text-xs text-slate-500">
          Customer Sheet · Tip: Use “Save as PDF” in your browser
        </div>
        <Button
          onClick={() => window.print()}
          className="gap-1.5"
          data-testid="print-now-btn"
        >
          <Printer className="h-4 w-4" /> Print
        </Button>
      </div>

      {/* ─────────── A4 single-page customer sheet ─────────── */}
      <div
        className="mx-auto max-w-[820px] px-8 py-6 print:px-6 print:py-2 text-[11px] leading-snug text-slate-900"
        data-testid="customer-sheet-page"
      >
        {/* ── HEADER ── */}
        <div className="border-b-2 border-slate-900 pb-2">
          <div className="flex items-start justify-between">
            <div>
              <div className="font-display text-[20px] font-bold tracking-tight leading-none">
                IVA MOTORS · YAMAHA
              </div>
              <div className="mt-0.5 text-[10px] text-slate-600">
                Authorised Yamaha Dealer
              </div>
            </div>
            <div className="text-right">
              <div className="text-[9px] uppercase tracking-[0.2em] text-slate-500">
                Customer Sheet
              </div>
              <div className="font-mono-data text-[14px] font-bold leading-tight">
                {sale.sr_number || "DRAFT"}
              </div>
            </div>
          </div>

          {/* Inline meta line: Date · Location · SR */}
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-0.5 text-[10.5px] text-slate-700">
            <MetaInline label="Date" value={sheetDate} />
            <span className="text-slate-300">·</span>
            <MetaInline
              label="Location"
              value={loc ? `${loc.name} (${loc.code})` : "—"}
            />
            <span className="text-slate-300">·</span>
            <MetaInline label="SR No" value={sale.sr_number} />
          </div>
        </div>

        {/* ── EXECUTIVE / ENQUIRY HEADER BLOCK ── */}
        <div className="mt-2 grid grid-cols-2 gap-x-6 gap-y-0.5 border-b border-slate-300 pb-2">
          <KV k="Sales Executive" v={dse?.name} />
          <KV k="Enquiry Sub Source" v={findM("enquiry_sub_source", sale.enquiry?.enquiry_sub_source_id)?.name} />
          <KV k="Enquiry No" v={sale.enquiry?.enquiry_number} />
          <KV k="Booking Date" v={fmtDate(sale.enquiry?.booking_date)} />
          <KV k="Enquiry Source" v={findM("enquiry_source", sale.enquiry?.enquiry_source_id)?.name} />
          <KV k="Promised Delivery" v={fmtDate(sale.delivery?.promised_date)} />
        </div>

        {/* ── CUSTOMER INFORMATION ── */}
        <Section title="Customer Information">
          <div className="grid grid-cols-2 gap-x-6 gap-y-0.5">
            <KV k="Name" v={sale.customer?.name} />
            <KV k="GST No" v={sale.customer?.gst_no} />
            <KV k="Mobile" v={sale.customer?.phone} />
            <KV k="Alt Mobile" v={sale.customer?.alt_phone} />
            <KV k="Email" v={sale.customer?.email} />
            <KV k="DOB" v={fmtDate(sale.customer?.dob)} />
            <KV k="Aadhaar" v={sale.customer?.aadhaar} />
            <KV k="Pin" v={sale.customer?.pin_code} />
            <KV k="At / PO" v={sale.customer?.at_po} />
            <KV k="City" v={sale.customer?.city} />
            <KV k="District" v={sale.customer?.district} />
            <KV k="State" v={sale.customer?.state} />
          </div>
          <div className="mt-0.5 grid grid-cols-[110px_1fr] gap-x-3">
            <span className="text-slate-500">Address</span>
            <span className="text-slate-900">{dash(sale.customer?.address)}</span>
          </div>
        </Section>

        {/* ── FINANCE & INSURANCE ── */}
        <Section title="Finance & Insurance">
          <div className="grid grid-cols-2 gap-x-6 gap-y-0.5">
            <KV
              k="Buy Mode"
              v={
                sale.finance?.buy_mode === "finance"
                  ? "Finance"
                  : sale.finance?.buy_mode === "cash"
                    ? "Cash"
                    : "—"
              }
            />
            <KV k="Insurance Nominee" v={sale.insurance?.nominee_name} />
            <KV
              k="Finance Co."
              v={findM("finance_company", sale.finance?.finance_company_id)?.name}
            />
            <KV k="Nominee Relation" v={sale.insurance?.relation} />
            <KV k="DO Number" v={sale.finance?.do_number} />
            <KV
              k="Nominee Age"
              v={sale.insurance?.nominee_age != null ? String(sale.insurance.nominee_age) : null}
            />
            {isV2 ? (
              <KV
                k="Dealer Disbursement Amount"
                v={sale.finance?.dealer_disbursement_amount != null
                  ? inr(sale.finance.dealer_disbursement_amount)
                  : null}
              />
            ) : (
              <KV k="Down Payment" v={inr(sale.payment?.down_payment)} />
            )}
            <KV k="Insurance" v={insuranceLabel} />
            <KV k="" v="" />
            <KV k="" v="" />
          </div>
        </Section>

        {/* ── REGISTRATION + VEHICLE side-by-side ── */}
        <div className="mt-2 grid grid-cols-2 gap-x-6">
          <SubSection title="Registration Details">
            <KV k="Choice Number" v={sale.registration?.choice_no} />
            <KV k="Registration Type" v={regnTypeLabel} />
            <KV k="RTO Office" v={sale.registration?.rto_office} />
          </SubSection>
          <SubSection title="Vehicle Details">
            <KV k="Model" v={sale.vehicle?.model} />
            <KV k="VIN" v={sale.vehicle?.vin} />
            <KV k="Color Code" v={sale.vehicle?.color_code || sale.vehicle?.color} />
            <KV k="Engine" v={sale.vehicle?.engine_no} />
            <KV k="WRC Number" v={sale.vehicle?.wrc_no} />
          </SubSection>
        </div>

        {/* ── VALUE ADDED SERVICES ── */}
        <Section title="Value Added Services">
          <div className="grid grid-cols-2 gap-x-6 gap-y-0.5">
            <KV k="Protection Plus" v={vasLabel("vas_protection_plus", sale.vas?.pp_id, "pp_opted")} />
            <KV k="AMC" v={vasLabel("vas_amc", sale.vas?.amc_id, "amc_opted")} />
            <KV k="RSA" v={vasLabel("vas_rsa", sale.vas?.rsa_id, "rsa_opted")} />
            <KV k="Accessories" v={accessoriesSummary} />
          </div>
        </Section>

        {/* ── PAYMENT SUMMARY + HISTORY (side by side) ── */}
        <div className="mt-2 grid grid-cols-[1.1fr_1fr] gap-x-6">
          <div>
            <SectionTitle>Payment Summary</SectionTitle>
            <div className="mt-1 grid grid-cols-[1fr_auto] gap-x-3 gap-y-0.5 text-[11px]">
              <PayRow k="Ex-showroom" v={inr(sale.payment?.exshowroom)} />
              <PayRow k="Registration" v={inr(sale.payment?.registration)} />
              <PayRow k="Hypothecation" v={inr(sale.payment?.hypothecation)} />
              <PayRow k="Insurance" v={inr(sale.payment?.insurance)} />
              <PayRow k="Protection Plus" v={vasAmount("pp", "pp_opted")} />
              <PayRow k="AMC" v={vasAmount("amc", "amc_opted")} />
              <PayRow k="RSA" v={vasAmount("rsa", "rsa_opted")} />
              {!isV2 && <PayRow k="Accessories" v={inr(sale.payment?.accessories)} />}
              <PayRow k="Admin & Incidental" v={inr(sale.payment?.admin_incidental)} />
            </div>
            {!isV2 && discountInfo.applied > 0 && (
              <div className="mt-1 flex items-center justify-between border-t border-dashed border-slate-300 pt-1 text-[11px]">
                <span className="font-semibold text-rose-700" data-testid="cs-discount-label">
                  {discountInfo.label}
                </span>
                <span className="font-mono-data font-semibold text-rose-700" data-testid="cs-discount-value">
                  − {inr(discountInfo.applied)}
                </span>
              </div>
            )}
            {isV2 ? (
              <>
                <div className="mt-1 flex items-center justify-between border-t-2 border-slate-900 pt-1 text-[11.5px]">
                  <span className="font-semibold uppercase tracking-wider">Gross Amount</span>
                  <span className="font-mono-data font-bold" data-testid="cs-gross">{inr(v2.gross)}</span>
                </div>
                <div className="mt-0.5 flex items-center justify-between text-[11px]">
                  <span className="text-slate-600">Accessories</span>
                  <span className="font-mono-data font-semibold">{inr(v2.accessories)}</span>
                </div>
                <div className="mt-1 flex items-center justify-between border-t-2 border-slate-900 pt-1 text-[11.5px]">
                  <span className="font-semibold uppercase tracking-wider">Net On-Road Price</span>
                  <span className="font-mono-data font-bold" data-testid="cs-net-on-road">{inr(v2.netOnRoad)}</span>
                </div>
                {v2.discount > 0 && (
                  <div className="mt-0.5 flex items-center justify-between text-[11px]">
                    <span className="text-rose-700 font-semibold">
                      Discount
                      {v2.discountConfig?.mode === "pct" && v2.discountConfig?.value
                        ? ` (${v2.discountConfig.value}%)` : ""}
                    </span>
                    <span className="font-mono-data font-semibold text-rose-700" data-testid="cs-discount-v2">
                      − {inr(v2.discount)}
                    </span>
                  </div>
                )}
                {v2.isFinance && (
                  <div className="mt-0.5 flex items-center justify-between text-[11px]">
                    <span className="text-slate-600">Dealer Disbursement Amount (DDA)</span>
                    <span className="font-mono-data font-semibold">
                      {v2.dda > 0 ? `− ${inr(v2.dda)}` : "—"}
                    </span>
                  </div>
                )}
                <div className="mt-1 flex items-center justify-between rounded-md bg-blue-50 border border-blue-200 px-2 py-1 text-[11.5px]">
                  <span className="font-bold uppercase tracking-wider text-blue-900">
                    Total To Be Received From Customer
                  </span>
                  <span className="font-mono-data font-bold text-blue-900" data-testid="cs-receivable">
                    {inr(v2.receivable)}
                  </span>
                </div>
              </>
            ) : (
              <>
                <div className="mt-1 flex items-center justify-between border-t-2 border-slate-900 pt-1 text-[11.5px]">
                  <span className="font-semibold uppercase tracking-wider">Total Price</span>
                  <span className="font-mono-data font-bold" data-testid="cs-total-price">{inr(totalPrice)}</span>
                </div>
                <div className="mt-0.5 flex items-center justify-between text-[11px]">
                  <span className="text-slate-600">Down Payment</span>
                  <span className="font-mono-data font-semibold">
                    {inr(sale.payment?.down_payment)}
                  </span>
                </div>
                <div className="mt-0.5 flex items-center justify-between text-[11px]">
                  <span className="text-slate-600">Accessories</span>
                  <span className="font-mono-data font-semibold">
                    {inr(sale.payment?.accessories)}
                  </span>
                </div>
                <div className="mt-1 flex items-center justify-between rounded-md bg-blue-50 border border-blue-200 px-2 py-1 text-[11.5px]">
                  <span className="font-bold uppercase tracking-wider text-blue-900">
                    Total To Be Received From Customer
                  </span>
                  <span className="font-mono-data font-bold text-blue-900">
                    {inr((Number(sale.payment?.down_payment) || 0) + (Number(sale.payment?.accessories) || 0))}
                  </span>
                </div>
              </>
            )}
          </div>

          <div>
            <SectionTitle>Payment History</SectionTitle>
            <table className="mt-1 w-full border-collapse text-[10.5px]">
              <thead>
                <tr className="border-b border-slate-300 text-left text-slate-500">
                  <th className="py-0.5 font-semibold">Date</th>
                  <th className="py-0.5 text-right font-semibold">Amount</th>
                  <th className="py-0.5 font-semibold">Mode</th>
                  <th className="py-0.5 font-semibold">Receipt No</th>
                </tr>
              </thead>
              <tbody>
                {receipts.length === 0 ? (
                  <tr>
                    <td className="py-1 text-slate-400" colSpan={4}>
                      No payments recorded yet
                    </td>
                  </tr>
                ) : (
                  receipts.map((r, i) => (
                    <tr
                      key={r.id || i}
                      className="border-b border-slate-100 last:border-0"
                    >
                      <td className="py-0.5 font-mono-data">
                        {fmtDate(r.received_on) || "—"}
                      </td>
                      <td className="py-0.5 text-right font-mono-data">
                        {inr(r.amount)}
                      </td>
                      <td className="py-0.5 uppercase">{dash(r.mode)}</td>
                      <td className="py-0.5 font-mono-data">
                        {dash(r.receipt_no)}
                      </td>
                    </tr>
                  ))
                )}
                {receipts.length > 0 && (
                  <tr className="border-t border-slate-300">
                    <td className="py-0.5 font-semibold">Total</td>
                    <td className="py-0.5 text-right font-mono-data font-semibold">
                      {inr(
                        receipts.reduce(
                          (s, r) => s + (Number(r.amount) || 0),
                          0,
                        ),
                      )}
                    </td>
                    <td colSpan={2}></td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── DELIVERY ── */}
        <Section title="Delivery">
          <div className="grid grid-cols-2 gap-x-6 gap-y-0.5">
            <KV k="Promised Date" v={fmtDate(sale.delivery?.promised_date)} />
            <KV k="Promised Time" v={sale.delivery?.promised_time} />
          </div>
          {sale.delivery?.notes && (
            <div className="mt-0.5 grid grid-cols-[110px_1fr] gap-x-3">
              <span className="text-slate-500">Notes</span>
              <span className="text-slate-900">{sale.delivery.notes}</span>
            </div>
          )}
        </Section>

        {/* ── SIGNATURES ── */}
        <div className="mt-6 grid grid-cols-2 gap-12 text-[10px]">
          <div className="border-t border-slate-400 pt-1 text-center">
            Customer Signature
          </div>
          <div className="border-t border-slate-400 pt-1 text-center">
            For IVA Motors · Yamaha
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          @page { size: A4; margin: 10mm; }
          body { background: white; }
        }
      `}</style>
    </div>
  );
}

// ──────────────── Helpers ────────────────
function SectionTitle({ children }) {
  return (
    <div className="text-[9px] font-bold uppercase tracking-[0.2em] text-slate-600 border-b border-slate-300 pb-0.5">
      {children}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mt-2">
      <SectionTitle>{title}</SectionTitle>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function SubSection({ title, children }) {
  return (
    <div>
      <SectionTitle>{title}</SectionTitle>
      <div className="mt-1 space-y-0.5">{children}</div>
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div className="grid grid-cols-[110px_1fr] gap-x-3 text-[11px]">
      <span className="text-slate-500">{k}</span>
      <span className="text-slate-900 truncate">{dash(v)}</span>
    </div>
  );
}

function MetaInline({ label, value }) {
  return (
    <span className="inline-flex items-baseline gap-1">
      <span className="text-[9px] uppercase tracking-wider text-slate-500">
        {label}
      </span>
      <span className="font-mono-data text-[11px] font-semibold text-slate-900">
        {dash(value)}
      </span>
    </span>
  );
}

function PayRow({ k, v }) {
  return (
    <>
      <span className="text-slate-600">{k}</span>
      <span className="text-right font-mono-data text-slate-900">{v}</span>
    </>
  );
}
